import type { GetMessagesQueryVariables } from '#src/graphql/generated';
import type { EditorJsData } from './editorjs-types';

import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  Popover,
  Portal,
  Button,
  Spinner,
  VStack,
  Textarea,
  Text,
  HStack,
} from '@chakra-ui/react';
import React from 'react';
import { FiZap } from 'react-icons/fi';

import { parseEditorJs } from './editorjs-types';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { toaster } from '#src/components/ui/toaster';
import { QUEUE_EXECUTE_PROCESSOR } from '#src/graphql/mutations/queue-execute-processor';
import { GET_PROCESSOR_INSTANCE } from '#src/graphql/queries/processor-instance';
import { persistProcessorFailureAsDocumentRootMessage } from '#src/utils/persist-processor-failure-message';

const DOCUMENT_AI_EDIT_PROCESSOR_ID = 'edit-document-prompt';

/** Keep in sync with edit-document-prompt.ts DEFAULT_EDIT_SUMMARY_MESSAGE */
const FALLBACK_AI_EDIT_TOAST_DESCRIPTION =
  'AI edit applied to editor; save to persist.';

const DEFERRED_EDIT_POLL_MS = 2000;
const DEFERRED_EDIT_MAX_MS = 15 * 60 * 1000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTerminalProcessorInstance(row: {
  isActive: boolean;
  value?: unknown;
  error?: string | null;
}): boolean {
  return !row.isActive && (row.error != null || row.value != null);
}

export interface PromptCommandBaseProps {
  /** Document ID - required for processing prompts */
  documentId?: string;
  /** Whether this is a new document (not yet saved) */
  isNewDocument: boolean;
  /** Optional tuned block id to scope the AI request. */
  focusBlockId?: string | null;
  /** Callback to set the editor value with the AI-edited document (user confirms and saves manually) */
  onValueUpdate: (value: EditorJsData | null) => void;
  /** Current editor JSON string so the server can apply edits on unsaved changes. */
  getEditorSnapshot?: () => string | null;
  /** OpenAI chat model id for this request; must be in the deployment allowlist. */
  model?: string;
  /**
   * Document message thread root to append this edit to in the chat log (server
   * `recordDocumentAiEditMessages`). Omit to start a new thread.
   */
  threadRootId?: string | null;
  /** When set, document messages are refetched during / after deferred processor completion. */
  refetchMessages?: () => void | Promise<unknown>;
}

export function usePromptSubmit({
  documentId,
  isNewDocument,
  focusBlockId,
  onValueUpdate,
  getEditorSnapshot,
  model,
  threadRootId,
  refetchMessages,
}: PromptCommandBaseProps) {
  const apolloClient = useApolloClient();
  const [queueExecuteProcessor, { loading: processingPrompt }] = useMutation(
    QUEUE_EXECUTE_PROCESSOR,
  );

  const documentMessagesVariables = React.useMemo<GetMessagesQueryVariables>(
    () => ({
      input: {
        contextFilters: [{ name: 'documentId', value: documentId ?? '' }],
      },
    }),
    [documentId],
  );

  const disabled = processingPrompt || isNewDocument;

  const persistDocError = React.useCallback(
    async (message: string, instanceId?: string | null) => {
      if (!documentId) return;
      const persist = await persistProcessorFailureAsDocumentRootMessage(
        apolloClient,
        {
          documentId,
          errorMessage: message,
          processorInstanceId: instanceId,
          getMessagesVariables: documentMessagesVariables,
        },
      );
      if (!persist.ok) {
        toaster.create({
          title: 'Could not save error to messages',
          description: persist.error,
          type: 'warning',
        });
      }
    },
    [apolloClient, documentId, documentMessagesVariables],
  );

  const applyEditFromStoredResult = React.useCallback(
    async (stored: unknown, instanceId: string | undefined) => {
      if (stored == null) {
        await persistDocError('Processor returned no result', instanceId);
        return;
      }
      const inner = stored as {
        success?: boolean;
        error?: string;
        value?: unknown;
        message?: unknown;
      };
      if (inner.success === false) {
        await persistDocError(inner.error ?? 'Request failed', instanceId);
        return;
      }
      const value = typeof inner.value === 'string' ? inner.value : undefined;
      if (typeof value === 'string') {
        const parsed = parseEditorJs(value);
        if (!parsed) {
          await persistDocError(
            'Processor did not return valid Editor.js JSON',
            instanceId,
          );
          return;
        }
        onValueUpdate(parsed);
        const summary =
          typeof inner.message === 'string' ? inner.message.trim() : '';
        toaster.create({
          title: 'Success',
          description: summary || FALLBACK_AI_EDIT_TOAST_DESCRIPTION,
        });
        return;
      }
      await persistDocError(
        'Processor did not return document value',
        instanceId,
      );
    },
    [onValueUpdate, persistDocError],
  );

  const pollDeferredEditCompletion = React.useCallback(
    (instanceId: string) => {
      void (async () => {
        const started = Date.now();
        try {
          while (Date.now() - started < DEFERRED_EDIT_MAX_MS) {
            const { data, error: queryError } = await apolloClient.query({
              query: GET_PROCESSOR_INSTANCE,
              variables: { id: instanceId },
              fetchPolicy: 'network-only',
            });
            if (queryError) {
              await sleep(DEFERRED_EDIT_POLL_MS);
              continue;
            }
            const row = data?.processorInstance;
            if (row != null && refetchMessages) {
              await refetchMessages();
            }
            if (row != null && isTerminalProcessorInstance(row)) {
              if (row.error != null && String(row.error).length > 0) {
                await persistDocError(String(row.error), row.id);
                return;
              }
              await applyEditFromStoredResult(row.value, row.id);
              return;
            }
            await sleep(DEFERRED_EDIT_POLL_MS);
          }
          await persistDocError(
            'AI edit timed out while waiting for the processor to finish.',
            instanceId,
          );
          toaster.create({
            title: 'AI edit timed out',
            description:
              'The edit may still complete; refresh the page or open messages to check.',
            type: 'warning',
          });
        } catch (error) {
          const description =
            error instanceof Error ? error.message : 'Unknown error';
          await persistDocError(description, instanceId);
          toaster.create({
            title: 'Error',
            description: `Error waiting for AI edit: ${description}`,
            type: 'error',
          });
        }
      })();
    },
    [apolloClient, refetchMessages, persistDocError, applyEditFromStoredResult],
  );

  const handleSubmit = React.useCallback(
    async (promptText: string): Promise<boolean> => {
      if (!promptText.trim()) return false;
      if (!documentId) {
        toaster.create({
          title: 'Error',
          description: 'Save the document first to use AI modifications.',
          type: 'error',
        });
        return false;
      }

      try {
        const snap = getEditorSnapshot?.() ?? null;
        const { data } = await queueExecuteProcessor({
          variables: {
            input: {
              processorId: DOCUMENT_AI_EDIT_PROCESSOR_ID,
              variables: {
                input: promptText.trim(),
                documentId,
                ...(snap && snap.length > 0
                  ? { currentEditorValue: snap }
                  : {}),
                ...(focusBlockId ? { focusBlockId } : {}),
                ...(model ? { model } : {}),
                ...(threadRootId?.trim()
                  ? { threadRootId: threadRootId.trim() }
                  : {}),
              },
            },
          },
        });

        const q = data?.queueExecuteProcessor;
        if (!q?.queued) {
          toaster.create({
            title: 'Could not queue AI edit',
            description: q?.message ?? 'Unknown error',
            type: 'error',
          });
          return false;
        }
        const pid = q.processorInstanceId?.trim();
        if (!pid) {
          toaster.create({
            title: 'Could not queue AI edit',
            description: 'Missing processor instance id from server.',
            type: 'error',
          });
          return false;
        }
        pollDeferredEditCompletion(pid);
        return true;
      } catch (error) {
        const description =
          error instanceof Error ? error.message : 'Unknown error';
        if (documentId) {
          const persist = await persistProcessorFailureAsDocumentRootMessage(
            apolloClient,
            {
              documentId,
              errorMessage: description,
              processorInstanceId: undefined,
              getMessagesVariables: documentMessagesVariables,
            },
          );
          if (!persist.ok) {
            toaster.create({
              title: 'Could not save error to messages',
              description: persist.error,
              type: 'warning',
            });
          }
        } else {
          toaster.create({
            title: 'Error',
            description: `Error applying AI edit: ${description}`,
            type: 'error',
          });
        }
        return false;
      }
    },
    [
      apolloClient,
      documentId,
      documentMessagesVariables,
      focusBlockId,
      model,
      threadRootId,
      getEditorSnapshot,
      queueExecuteProcessor,
      pollDeferredEditCompletion,
    ],
  );

  return { handleSubmit, disabled, processingPrompt };
}

/**
 * Inline AI prompt input for the document page footer (textarea + Apply).
 */
export function PromptFooterInput(props: PromptCommandBaseProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit(props);

  const onSubmit = async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    await handleSubmit(text);
  };

  return (
    <ChatComposerRow
      value={prompt}
      onChange={setPrompt}
      onSubmit={onSubmit}
      placeholder={
        isNewDocument
          ? 'Save the document first to use AI modifications'
          : 'e.g., Summarize this content, Translate to Spanish, Fix grammar errors...'
      }
      disabled={disabled}
      loading={processingPrompt}
    />
  );
}

/**
 * Prompt button (popover) for use where inline footer is not shown.
 */
export function PromptButton(props: PromptCommandBaseProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit(props);

  const title = isNewDocument
    ? 'Save the document first to use AI modifications'
    : undefined;

  const onPopoverSubmit = async () => {
    if (!prompt.trim()) return;
    const promptText = prompt.trim();
    setPrompt('');
    setIsOpen(false);
    await handleSubmit(promptText);
  };

  const handleCancel = () => {
    setPrompt('');
    setIsOpen(false);
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      positioning={{ placement: 'bottom-start' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="AI prompt to edit document"
          title={title ?? 'Use AI to edit this document'}
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        >
          {processingPrompt ? (
            <Spinner size="sm" />
          ) : (
            <FiZap style={{ width: '24px', height: '24px' }} />
          )}
        </button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="400px"
            p={4}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                AI Prompt
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Provide instructions for how AI should modify this document
              </Text>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Summarize this content, Translate to Spanish, Fix grammar errors..."
                rows={4}
                resize="vertical"
                minH="100px"
              />
              <HStack gap={2} justify="flex-end">
                <Button size="sm" variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={onPopoverSubmit}
                  disabled={!prompt.trim()}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
